import React, { useMemo } from "react";
import { Box, Divider, Drawer, IconButton, LinearProgress, Stack, Typography, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useQuery } from "@tanstack/react-query";
import { useIntl } from "react-intl";
import { fetchUsers } from "../../../fetchers/user";
import { fetchScheduleConsumption } from "../../../fetchers/cmmsAssetConsumption";
import { useToolServiceStatuses } from "../../../hooks/useToolServiceStatuses";
import { useScheduleResetLog } from "../../../hooks/useScheduleResetLog";
import cmmsMessages, { getTriggerBasisLabel } from "../../cmms/cmmsMessages";
import { CmmsErrorState, CmmsListSkeleton } from "../../cmms/components/CmmsStates";
import ScheduleResetButton from "../../cmms/components/ScheduleResetButton";
import ScheduleResetHistoryPanel from "../../cmms/components/ScheduleResetHistoryPanel";
import {
  buildScheduleReadingLabel,
  getLastResetAt,
  getReadingProgress,
  normalizeScheduleReading,
} from "../../cmms/scheduleReset";
import {
  cmmsTypeScale,
  getAssetHierarchyPalette,
  getPanelTitleSx,
  getSectionBoxSx,
  getSectionLabelSx,
} from "../../cmms/assetHierarchyStyles";
import messages from "../messages";

/*
 * One usage schedule: how far its counter has run, the control that puts it back to zero, and
 * every reset already recorded against it.
 *
 * A sibling of cmmsTools/ToolServiceDrawer rather than a reuse of it — that one is about a tool
 * and lists the schedules watching it, this is about a schedule. What is genuinely shared, the
 * reset button and the log panel, is imported from features/cmms where both live precisely so a
 * second mount point costs a wrapper.
 *
 * Always mounted, opened on `schedule`. That is what lets it animate out, while `enabled` on the
 * queries keeps them from firing before a row is clicked.
 *
 * Not gated on Cmms_Manager. Reading the log is Cmms_Field and the schedules route admits
 * [1,2,3,4]: a technician has to be able to see when a counter was last put back, even though
 * putting it back is not theirs to do. The gate is on the button inside.
 */
export default function ScheduleResetDrawer({ schedule, companyKey, onClose }) {
  const theme = useTheme();
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);
  const intl = useIntl();

  const open = Boolean(schedule);
  const isToolLane = Boolean(schedule?.toolId);

  /*
   * Two lanes, two reads, one shape.
   *
   * Both hooks are called on every render and gated by `enabled` — a hook behind an `if` is the
   * rule React does not bend. The asset lane's key is the one AssetUsageTab already uses, so the
   * asset panel's bar and this one share a cache and one invalidation corrects both.
   */
  const toolStatuses = useToolServiceStatuses(schedule?.toolId, { enabled: open && isToolLane });

  const consumptionQuery = useQuery(
    ["cmmsScheduleConsumption", companyKey, schedule?.id ?? null],
    () => fetchScheduleConsumption(companyKey, schedule.id),
    { enabled: open && !isToolLane && Boolean(companyKey && schedule?.id), staleTime: 60000 },
  );

  const reading = useMemo(() => {
    if (!schedule) return null;

    if (isToolLane) {
      // The tool's readings, not the company's — and the one measuring *this* schedule. A tool
      // can be watched in hours and in cycles at once, so picking the first would be picking
      // whichever the server ranked worst.
      const status = toolStatuses.statuses.find((s) => s.scheduleId === schedule.id);
      return normalizeScheduleReading({ toolStatus: status });
    }

    return normalizeScheduleReading({ consumption: consumptionQuery.data });
  }, [schedule, isToolLane, toolStatuses.statuses, consumptionQuery.data]);

  const resetLog = useScheduleResetLog(schedule?.id);

  // Same key and staleTime as everywhere else, so whichever page renders first warms the rest.
  const usersQuery = useQuery(["users"], () => fetchUsers(), { enabled: open, staleTime: 300000 });

  const userNameById = useMemo(() => {
    const map = new Map();
    (usersQuery.data ?? []).forEach((user) => {
      map.set(user.id, user.name ?? user.username ?? `#${user.id}`);
    });
    return map;
  }, [usersQuery.data]);

  const readingIsLoading = isToolLane ? toolStatuses.isLoading : consumptionQuery.isInitialLoading;
  const readingIsError = isToolLane ? toolStatuses.isError : consumptionQuery.isError;
  const readingError = isToolLane ? toolStatuses.error : consumptionQuery.error;
  const retryReading = () => (isToolLane ? toolStatuses.refetch() : consumptionQuery.refetch());

  const progress = getReadingProgress(reading);
  const readingLabel = buildScheduleReadingLabel(reading, intl);

  /*
   * The date the confirmation counts from, taken from the log rather than from the schedule.
   *
   * IntervalStartingDate is on the row this drawer was opened from and would be the easy answer.
   * It is the answer BE-15.19's fourth acceptance criterion forbids: a measurement anchor the
   * sweep and a basis change both move, saying nothing about who or when in reality.
   */
  const lastResetAt = getLastResetAt(resetLog.entries);

  let barColor = cmmsColors.activeText;
  if (progress.isOverdue) barColor = "error.main";
  else if (progress.barValue >= 80) barColor = "warning.main";

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", sm: 520 }, backgroundColor: cmmsColors.surface } }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Box sx={{ p: 2.5, borderBottom: `1px solid ${cmmsColors.border}` }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant={cmmsTypeScale.panelTitle} sx={getPanelTitleSx(cmmsColors)}>
                {schedule?.title}
              </Typography>
              <Typography variant="caption" sx={{ color: cmmsColors.mutedText }}>
                {getTriggerBasisLabel(schedule?.triggerBasis, intl)}
                {schedule?.toolName ? ` · ${schedule.toolName}` : ""}
              </Typography>
            </Box>
            <IconButton onClick={onClose} aria-label={intl.formatMessage(cmmsMessages.close)}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </Box>

        <Box sx={{ flex: 1, overflow: "auto", px: 2.5, py: 2 }}>
          <Typography variant={cmmsTypeScale.sectionLabel} sx={{ ...getSectionLabelSx(cmmsColors), display: "block" }}>
            {intl.formatMessage(messages.resetDrawerCounter)}
          </Typography>

          <Box sx={{ ...getSectionBoxSx(cmmsColors), mt: 1 }}>
            {readingIsLoading && <CmmsListSkeleton rows={2} height={28} />}

            {!readingIsLoading && readingIsError &&
              <CmmsErrorState
                error={readingError}
                fallback={intl.formatMessage(messages.resetDrawerReadingFailed)}
                onRetry={retryReading}
              />
            }

            {/*
              * No reading is an ordinary answer — AssetUsageCollector writes the samples this is
              * derived from, so a schedule created a minute ago has nothing yet. Saying so beats
              * drawing an empty bar, which reads as zero consumption rather than no measurement.
              */}
            {!readingIsLoading && !readingIsError && !reading &&
              <Typography variant="body2" sx={{ color: cmmsColors.mutedText }}>
                {intl.formatMessage(messages.resetDrawerNoReading)}
              </Typography>
            }

            {!readingIsLoading && !readingIsError && reading &&
              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {readingLabel}
                  </Typography>
                  {progress.label != null &&
                    <Typography
                      variant="body2"
                      sx={{ color: progress.isOverdue ? cmmsColors.dangerText : cmmsColors.text, fontWeight: 700, flexShrink: 0 }}
                    >
                      {progress.label}%
                      {progress.isOverdue ? ` ${intl.formatMessage(messages.resetDrawerOverdue)}` : ""}
                    </Typography>
                  }
                </Stack>

                <LinearProgress
                  variant="determinate"
                  value={progress.barValue}
                  color="inherit"
                  sx={{
                    height: 8,
                    borderRadius: 1,
                    backgroundColor: cmmsColors.trackBg,
                    "& .MuiLinearProgress-bar": { backgroundColor: barColor },
                  }}
                />
              </Stack>
            }

            {/*
              * Rendered whatever the reading did. A failed read must not take the reset with it —
              * the confirmation drops the figure and says so, which is the case
              * buildScheduleResetWarning's null branch exists for.
              */}
            <Box sx={{ mt: 2 }}>
              <ScheduleResetButton
                schedule={schedule}
                reading={reading}
                lastResetAt={lastResetAt}
                fullWidth
              />
            </Box>
          </Box>

          <Divider sx={{ my: 2.5 }} />

          <Typography variant={cmmsTypeScale.sectionLabel} sx={{ ...getSectionLabelSx(cmmsColors), display: "block", mb: 1 }}>
            {intl.formatMessage(cmmsMessages.resetLogTitle)}
          </Typography>

          {isToolLane &&
            <Typography variant="caption" sx={{ color: cmmsColors.mutedText, display: "block", mb: 1 }}>
              {intl.formatMessage(messages.resetDrawerToolHistoryNote)}
            </Typography>
          }

          <ScheduleResetHistoryPanel
            entries={resetLog.entries}
            // The user list is folded into the same skeleton rather than raced against it: letting
            // the entries land first would show "#1016" for a moment before it turned into a name.
            // A failed user read is not waited on — isInitialLoading clears on error, and the ids
            // are then the answer.
            isLoading={resetLog.isLoading || usersQuery.isInitialLoading}
            isError={resetLog.isError}
            error={resetLog.error}
            onRetry={() => resetLog.refetch()}
            isRetrying={resetLog.isFetching}
            userNameById={userNameById}
          />
        </Box>
      </Box>
    </Drawer>
  );
}
